export const VAULT_SESSION_LOCK_NAME = "ma-cave:decrypted-vault-session";

export const VAULT_SESSION_OCCUPIED_MESSAGE =
  "Ma Cave est déjà ouverte dans un autre onglet. Fermez-le puis réessayez.";

export const VAULT_SESSION_UNAVAILABLE_MESSAGE =
  "Ce navigateur ne permet pas de protéger le coffre entre les onglets. Utilisez un navigateur récent prenant en charge Web Locks.";

export class VaultSessionLockError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "VaultSessionLockError";
  }
}

export class VaultSessionOccupiedError extends VaultSessionLockError {
  constructor() {
    super(VAULT_SESSION_OCCUPIED_MESSAGE);
    this.name = "VaultSessionOccupiedError";
  }
}

export class VaultSessionLockUnavailableError extends VaultSessionLockError {
  constructor() {
    super(VAULT_SESSION_UNAVAILABLE_MESSAGE);
    this.name = "VaultSessionLockUnavailableError";
  }
}

export interface VaultBrowserLock {
  readonly name: string;
}

export interface VaultBrowserLockManager {
  request<T>(
    name: string,
    options: { mode: "exclusive"; ifAvailable: true },
    callback: (lock: VaultBrowserLock | null) => Promise<T> | T,
  ): Promise<T>;
}

type LockManagerProvider = () => VaultBrowserLockManager | null;

function browserLockManager(): VaultBrowserLockManager | null {
  if (typeof navigator === "undefined") return null;
  const locks = navigator.locks;
  return locks && typeof locks.request === "function"
    ? (locks as VaultBrowserLockManager)
    : null;
}

/**
 * Verrou de session, distinct des courtes transactions IndexedDB. Le callback
 * Web Locks reste volontairement en attente tant que la clé est en mémoire.
 */
export class VaultSessionLock {
  private held = false;
  private acquiring: Promise<void> | null = null;
  private requestLifetime: Promise<void> | null = null;
  private releaseHold: (() => void) | null = null;

  constructor(
    private readonly managerProvider: LockManagerProvider = browserLockManager,
  ) {}

  isHeld(): boolean {
    return this.held;
  }

  async acquire(): Promise<void> {
    if (this.held) return;
    if (this.acquiring) return this.acquiring;

    const manager = this.managerProvider();
    if (!manager) throw new VaultSessionLockUnavailableError();

    let decisionSettled = false;
    let resolveDecision!: (lock: VaultBrowserLock | null) => void;
    let rejectDecision!: (error: unknown) => void;
    const decision = new Promise<VaultBrowserLock | null>((resolve, reject) => {
      resolveDecision = resolve;
      rejectDecision = reject;
    });

    let resolveHold!: () => void;
    const hold = new Promise<void>((resolve) => {
      resolveHold = resolve;
    });

    const lifetime = Promise.resolve()
      .then(() => manager.request(
        VAULT_SESSION_LOCK_NAME,
        { mode: "exclusive", ifAvailable: true },
        async (lock) => {
          decisionSettled = true;
          resolveDecision(lock);
          if (lock) await hold;
        },
      ))
      .then(
        () => undefined,
        (error: unknown) => {
          if (!decisionSettled) rejectDecision(error);
          else {
            this.held = false;
            this.releaseHold = null;
          }
        },
      );
    this.requestLifetime = lifetime;

    const acquisition = (async () => {
      let lock: VaultBrowserLock | null;
      try {
        lock = await decision;
      } catch {
        throw new VaultSessionLockUnavailableError();
      }
      if (!lock) {
        await lifetime;
        throw new VaultSessionOccupiedError();
      }
      this.held = true;
      this.releaseHold = resolveHold;
    })();
    this.acquiring = acquisition;

    try {
      await acquisition;
    } finally {
      if (this.acquiring === acquisition) this.acquiring = null;
    }
  }

  async release(): Promise<void> {
    if (this.acquiring) {
      await this.acquiring.catch(() => undefined);
    }
    if (!this.held) return;

    const release = this.releaseHold;
    const lifetime = this.requestLifetime;
    this.held = false;
    this.releaseHold = null;
    release?.();
    await lifetime;
    if (this.requestLifetime === lifetime) this.requestLifetime = null;
  }
}

const browserVaultSessionLock = new VaultSessionLock();

export function isVaultSessionLockSupported(): boolean {
  return browserLockManager() !== null;
}

export function hasVaultSessionLock(): boolean {
  return browserVaultSessionLock.isHeld();
}

export async function acquireVaultSessionLock(): Promise<void> {
  await browserVaultSessionLock.acquire();
}

export async function releaseVaultSessionLock(): Promise<void> {
  await browserVaultSessionLock.release();
}
